import { useState, useCallback, useRef } from 'react';
import { useAuth0 } from "@auth0/auth0-react";
import { svc_getDebtAccounts } from "../services/account.service";
import { useResponseMessages } from "./useResponseMessage";
import { getDefaultPaymentAmount, getMinFixedAmount, getDefaultPaymentType } from '../utilities/account-utilities';

/**
 * Custom hook for managing refinance account state and operations
 * Similar to useSnowballAccounts but focused on refinance calculations
 */
export const useRefinanceAccounts = () => {
    const { getAccessTokenSilently, user } = useAuth0();
    const { addMessage } = useResponseMessages();
    
    const [debtAccounts, setDebtAccounts] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    
    // Use ref to track if accounts have been fetched to prevent unnecessary refetches
    const hasFetchedAccounts = useRef(false);

    /**
     * Adds default properties to each debt account for refinance calculations
     */
    const addAccountProps = useCallback((account, index) => {
        const calculatedPayment = getDefaultPaymentAmount(account);
        const paymentType = getDefaultPaymentType(account);
        
        return {
            ...account,
            index,
            // Ensure we have the basic fields needed for calculations
            balance: parseFloat(account.balance) || 0,
            monthly_payment: calculatedPayment, // Use calculated payment amount
            apr: parseFloat(account.apr) || 0,
            // Add payment calculation helpers
            minFixedAmount: getMinFixedAmount(account),
            defaultPaymentAmount: calculatedPayment,
            paymentType: paymentType,
            // Ensure minimum payment fields are available for dynamic calculation
            minimum_payment_percentage: parseFloat(account.minimum_payment_percentage) || 0.02,
            minimum_payment_low_balance: parseFloat(account.minimum_payment_low_balance) || 25
        };
    }, []);

    /**
     * Fetches debt accounts for refinance analysis
     */
    const getAccounts = useCallback(async () => {
        if (isLoading) return;
        
        setIsLoading(true);
        
        try {
            const accessToken = await getAccessTokenSilently();
            const { data: debtAccountsData } = await svc_getDebtAccounts(accessToken, user.sub);

            // Process debt accounts - filter out zero balances and sort by balance
            const filteredDebtAccounts = debtAccountsData
                .filter(account => account.balance > 0)
                .sort((a, b) => parseFloat(b.balance) - parseFloat(a.balance)) // Sort by balance descending
                .map(addAccountProps);

            setDebtAccounts(filteredDebtAccounts);
            hasFetchedAccounts.current = true;

            if (filteredDebtAccounts.length === 0) {
                addMessage({
                    data: 'No debt accounts with balances found. You can add accounts manually to proceed with calculations.'
                });
            } else {
                addMessage({
                    data: `Loaded ${filteredDebtAccounts.length} debt account(s) for refinance analysis.`
                });
            }

        } catch (error) {
            console.error('Error fetching accounts:', error);
            addMessage({
                error: 'There was an error fetching your accounts. You can still add accounts manually to proceed.'
            });
        } finally {
            setIsLoading(false);
        }
    }, [getAccessTokenSilently, user.sub, addMessage, addAccountProps, isLoading]);

    /**
     * Refetches accounts with user confirmation if accounts exist
     */
    const handleRefetchAccounts = useCallback(() => {
        const hasExistingAccounts = debtAccounts && debtAccounts.length > 0;
        
        if (hasExistingAccounts) {
            const confirmRefetch = window.confirm(
                "Are you sure you want to re-fetch your accounts? This will clear any manual accounts you've added."
            );
            if (!confirmRefetch) return;
        }
        
        hasFetchedAccounts.current = false;
        getAccounts();
    }, [debtAccounts, getAccounts]);

    /**
     * Updates a specific debt account
     */
    const updateAccount = useCallback((updatedAccount) => {
        setDebtAccounts(prevAccounts => {
            const updatedAccounts = [...prevAccounts];
            const accountIndex = updatedAccounts.findIndex(acc => acc.id === updatedAccount.id);
            
            if (accountIndex !== -1) {
                const calculatedPayment = getDefaultPaymentAmount(updatedAccount);
                
                updatedAccounts[accountIndex] = {
                    ...updatedAccount,
                    balance: parseFloat(updatedAccount.balance) || 0,
                    monthly_payment: calculatedPayment,
                    apr: parseFloat(updatedAccount.apr) || 0,
                    minFixedAmount: getMinFixedAmount(updatedAccount),
                    defaultPaymentAmount: calculatedPayment
                };
            }
            
            return updatedAccounts;
        });
    }, []);

    return {
        // State
        debtAccounts,
        isLoading,
        hasFetchedAccounts: hasFetchedAccounts.current,
        
        // Actions
        getAccounts,
        handleRefetchAccounts,
        updateAccount,
        setDebtAccounts // For external operations that need to update multiple accounts
    };
};